import React, { useState } from "react";
import { DeviceSummary } from "../types";

interface DeviceTableProps {
  devices: DeviceSummary[];
  onSelectDevice: (device: DeviceSummary) => void;
  onOpenEnrollModal: () => void;
}

export const DeviceTable: React.FC<DeviceTableProps> = ({
  devices,
  onSelectDevice,
  onOpenEnrollModal,
}) => {
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filtered = devices.filter((d) => {
    const matchesStatus =
      filterStatus === "all"
        ? true
        : filterStatus === "kill_switched"
        ? d.kill_switched
        : d.status === filterStatus;
    const matchesSearch =
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.mesh_ip.includes(searchTerm) ||
      (d.endpoint && d.endpoint.includes(searchTerm));
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="devices-table-container card">
      <div className="table-header">
        <div className="table-title">
          <h2>MikroTik Fleet Devices ({filtered.length})</h2>
          <p>Managed WireGuard nodes and hubs</p>
        </div>

        <div className="table-actions">
          <button className="btn btn-primary" onClick={onOpenEnrollModal}>
            + Enroll Router
          </button>
        </div>
      </div>

      <div className="table-controls">
        <div className="status-tabs">
          <button
            className={`tab-btn ${filterStatus === "all" ? "active" : ""}`}
            onClick={() => setFilterStatus("all")}
          >
            All ({devices.length})
          </button>
          <button
            className={`tab-btn ${filterStatus === "active" ? "active" : ""}`}
            onClick={() => setFilterStatus("active")}
          >
            Active ({devices.filter((d) => d.status === "active").length})
          </button>
          <button
            className={`tab-btn ${filterStatus === "pending" ? "active" : ""}`}
            onClick={() => setFilterStatus("pending")}
          >
            Pending ({devices.filter((d) => d.status === "pending").length})
          </button>
          <button
            className={`tab-btn ${filterStatus === "kill_switched" ? "active" : ""}`}
            onClick={() => setFilterStatus("kill_switched")}
          >
            Killed ({devices.filter((d) => d.kill_switched).length})
          </button>
        </div>

        <div className="search-box">
          <input
            id="device-search"
            name="device-search"
            type="text"
            placeholder="Search name, Mesh IP, endpoint..."
            aria-label="Search devices"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="device-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Mesh IP</th>
              <th>Endpoint / NAT</th>
              <th>Role</th>
              <th>RouterOS</th>
              <th>Config Ver</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-cell">
                  No fleet routers matched your criteria.
                </td>
              </tr>
            ) : (
              filtered.map((device) => (
                <tr
                  key={device.id}
                  className="table-row"
                  onClick={() => onSelectDevice(device)}
                >
                  <td>
                    <span
                      className={`status-dot ${
                        device.kill_switched
                          ? "killed"
                          : device.status === "active"
                          ? "online"
                          : "pending"
                      }`}
                    ></span>
                    <span className="status-label">
                      {device.kill_switched ? "Killed" : device.status}
                    </span>
                  </td>
                  <td className="font-semibold">{device.name}</td>
                  <td>
                    <code className="ip-badge">{device.mesh_ip}</code>
                  </td>
                  <td>{device.endpoint || "Unassigned"}</td>
                  <td>
                    <span className="role-tag">{device.role || "mesh"}</span>
                  </td>
                  <td>{device.ros_version || "—"}</td>
                  <td>v{device.config_version}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectDevice(device);
                      }}
                    >
                      Inspect
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
